// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BrandDetailsPageContext } from "@insite/content-library/Pages/BrandDetailsPage";
import MobiusLink, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import { totalSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import Tooltip from "@mcmc/MobiusOverrides/Tooltip";
import Cart from "@mcmc/XDL/Icons/Cart";
import Link from "@mcmc/XDL/Icons/Link";
import { commonBreadcrumbIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    setBreadcrumbActions,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface BrandDetailsLinksStyles {
    breadcrumbActionsWrapper?: InjectableCss;
    visitWebsiteIconStyle?: LinkPresentationProps;
    viewAllProductsIconStyle?: LinkPresentationProps;
}

export const linksStyles: BrandDetailsLinksStyles = {
    breadcrumbActionsWrapper: totalSectionGlobalStyles.breadcrumbActionsWrapper,
    visitWebsiteIconStyle: commonBreadcrumbIconStyle(Link, 14),
    viewAllProductsIconStyle: commonBreadcrumbIconStyle(Cart),
};

const styles = linksStyles;

const BrandDetailsLinks = ({ setBreadcrumbActions }: Props) => {
    const { value: brand } = useContext(BrandStateContext);

    useEffect(() => {
        if (!brand) {
            return;
        }

        setBreadcrumbActions({
            breadcrumbActions: (
                <StyledWrapper {...styles.breadcrumbActionsWrapper}>
                    <Tooltip
                        text={translate("Visit {0} Website").replace("{0}", brand.name)}
                        triggerAltText={translate("Visit {0} Website").replace("{0}", brand.name)}
                        placement="bottom-end"
                        triggerComponent={
                            <>
                                {brand.externalUrl && (
                                    <MobiusLink
                                        {...styles.visitWebsiteIconStyle}
                                        data-test-selector="brandExternalUrlLink"
                                        href={brand.externalUrl}
                                    ></MobiusLink>
                                )}
                            </>
                        }
                    ></Tooltip>
                    <Tooltip
                        text={translate("Shop All {0} Products").replace("{0}", brand.name)}
                        placement="bottom-end"
                        triggerAltText={translate("Shop All {0} Products").replace("{0}", brand.name)}
                        triggerComponent={
                            <MobiusLink
                                {...styles.viewAllProductsIconStyle}
                                data-test-selector="brandToProductPageLink"
                                href={brand.productListPagePath}
                            ></MobiusLink>
                        }
                    ></Tooltip>
                </StyledWrapper>
            ),
        });
        return () => {
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, []);

    return <></>;
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(BrandDetailsLinks),
    definition: {
        group: "Brand Details",
        icon: "Link",
        displayName: "Brand Details Links",
        allowedContexts: [BrandDetailsPageContext],
    },
};

export default widgetModule;
